import { useRegisterSeg } from "../lib/segRegistry";
import React from "react";
import { useQuery } from "@tanstack/react-query";
import { shortSeason } from "../lib/format";
import { api } from "../lib/api";
import { useShiftHold } from "../lib/useShiftHold";
import { useAutoPin } from "../lib/useAutoPin";

// Historical baseline — always present even before any season docs exist.
export const PWHL_SEASONS_PUBLIC = ["2026-27", "2025-26", "2024-25", "2024"];

const seasonSort = (a, b) => b.localeCompare(a);

// Live, backend-driven season list. New seasons created in the Owner area
// (Seasons) appear everywhere automatically (standings, schedule, leaderboards).
// Falls back to the historical baseline so nothing breaks if the API is empty.
export function useSeasonList() {
    const { data } = useQuery({
        queryKey: ["season-list"],
        queryFn: () => api.get("/seasons", { params: { visible_only: true } }).then((r) => r.data),
        staleTime: 5 * 60 * 1000,
    });
    const fromApi = (data || []).map((s) => s.season).filter(Boolean);
    const merged = Array.from(new Set([...fromApi, ...PWHL_SEASONS_PUBLIC]));
    return merged.sort(seasonSort);
}

export function SeasonSwitcher({ value, onChange, testIdPrefix, includeAllTime = false, pinnedKey: pinProp = null, onPin: onPinProp = null, pinReady = true }) {
    const list = useSeasonList();
    const { shift } = useShiftHold();
    const seasons = includeAllTime ? [...list, "all-time"] : list;
    const auto = useAutoPin(onPinProp ? null : `${testIdPrefix}-season`, value, onChange, seasons, pinReady);
    const onPin = onPinProp || auto.onPin;
    const pinnedKey = onPinProp ? pinProp : auto.pinnedKey;
    useRegisterSeg({ keys: seasons, value, onPin, pinnedKey });
    return (
        <div className="inline-flex rounded-lg border border-white/15 overflow-hidden bg-black/25" data-testid={`${testIdPrefix}-season-switcher`}>
            {seasons.map((s) => (
                <button
                    key={s}
                    onClick={(e) => { if ((e.shiftKey || shift) && onPin) onPin(s); onChange(s); }}
                    data-testid={`${testIdPrefix}-season-${s}`}
                    title={onPin ? "Click to select · Shift+click to pin as default (click the pinned one again with Shift to unpin)" : undefined}
                    style={value === s ? { color: "#D9BEF5", backgroundColor: "rgba(157,76,221,0.22)" } : undefined}
                    className={`relative px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-colors font-mono-stat ${
                        value === s ? "" : "text-[#C9A6F0] hover:text-white"
                    } ${onPin && shift && s !== (pinnedKey || value) ? "opacity-50" : ""}`}
                >
                    {s === "all-time" ? "ALL TIME" : shortSeason(s)}
                    {onPin && pinnedKey === s && <span className="absolute top-0.5 right-0.5 h-1.5 w-1.5 rounded-full bg-[#F5B301]" />}
                </button>
            ))}
        </div>
    );
}
